import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { updateDepartmentParent } from '../../../api/endpoints/corporate';
import type { Department } from '../../../api/types/corporate';
import { ApiErrorAlert } from '../../../components/ApiErrorAlert';

/** Quick "move" action wired to the dedicated CorporateDepartment/UpdateParent route (kept
 *  distinct from DepartmentFormDialog's full edit form per the migration guide's page
 *  inventory, which calls this out as its own action alongside add/edit/delete). */
export function DepartmentReparentDialog({
  open,
  department,
  options,
  excludeIds,
  onClose,
}: {
  open: boolean;
  department: Department | null;
  options: { id: number; label: string }[];
  excludeIds: Set<number>;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [newParentId, setNewParentId] = useState<number | ''>('');

  useEffect(() => {
    if (open) setNewParentId(department?.parentDepartmentId ?? '');
  }, [open, department]);

  const mutation = useMutation({
    mutationFn: () =>
      updateDepartmentParent({
        departmentId: department!.id,
        parentDepartmentId: newParentId === '' ? null : newParentId,
      }),
    onSuccess: () => {
      enqueueSnackbar('Üst departman güncellendi.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['corporate', 'departments'] });
      onClose();
    },
    onError: () => {
      /* surfaced via ApiErrorAlert below */
    },
  });

  const selectableOptions = options.filter((o) => !excludeIds.has(o.id));

  return (
    <Dialog open={open} onClose={() => !mutation.isPending && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle>Üst Departmanı Değiştir</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            "{department?.name}" için yeni üst departmanı seçin.
          </Typography>
          <TextField
            select
            fullWidth
            size="small"
            label="Üst Departman"
            value={newParentId}
            onChange={(e) => setNewParentId(e.target.value === '' ? '' : Number(e.target.value))}
          >
            <MenuItem value="">Kök (üst departman yok)</MenuItem>
            {selectableOptions.map((o) => (
              <MenuItem key={o.id} value={o.id}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
          <ApiErrorAlert error={mutation.error} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isPending}>
          Vazgeç
        </Button>
        <Button onClick={() => mutation.mutate()} variant="contained" disabled={mutation.isPending}>
          {mutation.isPending ? 'Kaydediliyor…' : 'Taşı'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
